import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Alert,
  Snackbar,
  Divider,
  IconButton,
  Stack
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useCallback, useMemo, useContext } from 'react';
import { db } from '../../config/firebase';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { ElementData } from '../../data/elementData';
import { ProjectData } from '../../data/projectData';
import { AuthContext } from '../../contexts/authContext';
import { USER_TYPES } from '../../constants/userTypes';
import { useQuery } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import LayoutFree from '../../assets/layout-free.webp';
import LayoutTree from '../../assets/layout-tree.webp';
import LayoutLayer from '../../assets/layout-layer.webp';

// Constants
const CARD_DATA = [
  {
    title: "Free",
    description: "A natural, flexible arrangement that adapts to connections.",
    subtitle: "Organic layout",
    value: "cose-bilkent",
    image: LayoutFree
  },
  {
    title: "Layered",
    description: "Arranges as many edges as possible into one direction by placing nodes into subsequent layers.",
    subtitle: "Network layouts",
    value: "layered",
    image: LayoutLayer
  },
  {
    title: "Tree",
    description: "Organization structure, family tree. Resembles a tree with outcomes that branch off from the original idea.",
    subtitle: "Hierarchical layouts",
    value: "mrtree",
    image: LayoutTree
  }
];

const INITIAL_PROJECT_DETAILS = {
  name: "",
  description: ""
};

const BUTTON_STATES = {
  IDLE: "Create",
  LOADING: "Creating..."
};

export const NewProjectDialog = ({ open, handleClose, uid, type }) => {
  const navigate = useNavigate();
  const { userProfile } = useContext(AuthContext);

  // Fetch projects to check count for FREE users
  const { data: projects } = useQuery({
    queryKey: ["loadmyprojects"],
    queryFn: async () => {
      const q = query(collection(db, "project"), where("owner", "==", uid));
      const data = await getDocs(q);
      const filteredData = data.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id
      }));
      return filteredData;
    },
    enabled: !!uid && open
  });

  // State management
  const [projectDetails, setProjectDetails] = useState(INITIAL_PROJECT_DETAILS);
  const [createButtonText, setCreateButtonText] = useState(BUTTON_STATES.IDLE);
  const [createButtonIsDisabled, setCreateButtonIsDisabled] = useState(false);
  const [selectedCard, setSelectedCard] = useState(0);
  const [error, setError] = useState(null);

  // Memoized values
  const isFormValid = useMemo(() => {
    return projectDetails.name && projectDetails.name.trim() !== "";
  }, [projectDetails.name]);

  // Event handlers
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setProjectDetails(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleCardClick = useCallback((cardIndex) => {
    setSelectedCard(cardIndex);
    // When a card is clicked, update selectedCard and set the layout in projectDetails
    const selectedLayout = CARD_DATA[cardIndex]?.value;
    setProjectDetails(prev => ({
      ...prev,
      layout: selectedLayout
    }));
    console.log("Selected Layout: " + selectedLayout);
  }, []);

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const resetForm = useCallback(() => {
    setProjectDetails(INITIAL_PROJECT_DETAILS);
    setSelectedCard(0);
    setError(null);
  }, []);

  const handleCloseDialog = useCallback((event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    resetForm();
    handleClose();
  }, [resetForm, handleClose]);

  const createProject = useCallback(async (projectData) => {
    try {
      const newProjectRef = collection(db, 'project');
      const res = await addDoc(newProjectRef, projectData);
      return res.id;
    } catch (error) {
      console.error('Error creating project:', error);
      throw new Error('Failed to create project. Please try again.');
    }
  }, []);

  const handleConfirm = useCallback(async (event) => {
    event.preventDefault();

    if (!isFormValid) {
      event.target[0].focus();
      return;
    }

    // Check if FREE user has reached project limit
    const isFreeUser = userProfile?.type === USER_TYPES.FREE;
    const projectCount = projects?.length || 0;
    const maxProjectsForFree = 3;
    
    if (isFreeUser && projectCount >= maxProjectsForFree) {
      setError(`Free users can create up to ${maxProjectsForFree} projects. Please upgrade to create more projects.`);
      return;
    }

    setCreateButtonIsDisabled(true);
    setCreateButtonText(BUTTON_STATES.LOADING);
    setError(null);

    try {
      // Prepare project data
      const newElementData = {
        group: "nodes",
        data: {
          ...ElementData,
          id: 1,
          name: "Hi! I'm a node",
          createdon: new Date().toISOString(),
        },
      };

      const projectData = {
        ...ProjectData,
        name: projectDetails.name.trim(),
        description: projectDetails.description.trim(),
        owner: uid,
        createdon: new Date().toISOString(),
        layout: CARD_DATA[selectedCard].value,
      };

      const projectId = await createProject(projectData);
      const newElementRef = collection(db, 'project', projectId, 'elements');
      await addDoc(newElementRef, newElementData);

      navigate(`/project/${projectId}`);
      handleCloseDialog();
    } catch (error) {
      setError(error.message);
    } finally {
      setCreateButtonIsDisabled(false);
      setCreateButtonText(BUTTON_STATES.IDLE);
    }
  }, [projectDetails, isFormValid, uid, createProject, navigate, handleCloseDialog, userProfile, projects]);

  // Render card component
  const renderCard = useCallback((card, index) => (
    <Grid item xs={12} sm={4} key={index} sx={{ display: 'flex' }}>
      <Card
        variant="outlined"
        sx={{
          cursor: 'pointer',
          border: selectedCard === index ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.12)',
          backgroundColor: selectedCard === index ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            border: '2px solid #1976d2',
            backgroundColor: 'rgba(25, 118, 210, 0.04)'
          },
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          height: '100%',
          minHeight: 180, // adjust as needed for your content
        }}
        onClick={() => handleCardClick(index)}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 12 }}>
            {card.subtitle}
          </Typography>
          <Typography variant="h6" component="div">
            {card.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {card.description}
          </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Box
            component="img"
            src={card.image}
            alt={card.title}
            sx={{
              width: 128,
              height: 128,
              objectFit: 'contain',
              mt: 2,
              borderRadius: 8, // adds rounded corners (theme.spacing(2), i.e. 16px)
              boxShadow: 1
            }}
          />
        </Box>
        </CardContent>
      </Card>
    </Grid>
  ), [selectedCard, handleCardClick]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0px 12px 40px rgba(15, 23, 42, 0.18)',
            // Add extra space at top/bottom on mobile to avoid browser bars covering content
            m: { xs: 'env(safe-area-inset-top, 16px) 0 env(safe-area-inset-bottom, 16px) 0', sm: 3 },
            width: { xs: '100vw', sm: 'auto' },
            maxWidth: { xs: '100vw', sm: '800px' },
            maxHeight: { xs: 'calc(100vh - 32px)', sm: '90vh' }, // 16px top + 16px bottom reserve
            minHeight: { xs: 'calc(100vh - 32px)', sm: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            // For safe area on iOS/Android
            pt: { xs: 'env(safe-area-inset-top, 16px)', sm: 0 },
            pb: { xs: 'env(safe-area-inset-bottom, 16px)', sm: 0 },
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2.5,
            pr: 2,
            pl: 3
          }}
        >
          <Box>
            <Typography variant="h6" component="div">
              Create Project
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a name and default layout to start exploring your ideas.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close project dialog"
            onClick={() => handleCloseDialog()}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <Box
          component="form"
          autoComplete="off"
          onSubmit={handleConfirm}
          noValidate
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0
          }}
        >
          <DialogContent
            dividers
            sx={{
              px: { xs: 2.5, sm: 3 },
              py: { xs: 2.5, sm: 3 },
              bgcolor: 'background.default',
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
              maxHeight: { 
                xs: 'calc(100vh - 124px - 32px)', // reserve space for top/bottom padding
                sm: 'unset'
              },
              // 124px = dialog title + dialog actions + paddings; 32px = 16 + 16 px reserved mobile margin
            }}
          >
            <Stack spacing={3}>
              <TextField
                size="small"
                required
                autoFocus
                name="name"
                label="Name"
                type="text"
                fullWidth
                value={projectDetails.name}
                inputProps={{ maxLength: 100 }}
                onChange={handleChange}
                error={!isFormValid && projectDetails.name !== ""}
                helperText={!isFormValid && projectDetails.name !== "" ? "Project name is required" : `${(projectDetails?.name || '').length}/100 characters`}
              />

              {type === 2 && (
                <Alert severity="warning">
                  You're creating a project without registration. The only way to access it is with the generated URL.
                  Copy and save it. Projects of this type will be automatically deleted after 30 days of last access.
                </Alert>
              )}

              {type === 2 && (
                <TextField
                  size="small"
                  margin="dense"
                  name="url"
                  label="URL"
                  type="text"
                  fullWidth
                />
              )}

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Select default layout for your project.
                </Typography>
                <Grid container spacing={1}>
                  {CARD_DATA.map(renderCard)}
                </Grid>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Can't decide or need a different layout? That's fine. The layout can be changed at any time in project properties.
                </Typography>
              </Box>
            </Stack>
          </DialogContent>

          <Divider />
          <DialogActions
            sx={{
              px: { xs: 2.5, sm: 3 },
              py: { xs: 2.5, sm: 3 },
              bgcolor: 'background.paper',
              justifyContent: 'flex-end',
              gap: 1.5
            }}
          >
            <Button
              onClick={() => handleCloseDialog()}
              disabled={createButtonIsDisabled}
              variant="outlined"
              color="inherit"
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createButtonIsDisabled || !isFormValid}
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
            >
              {createButtonText}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};


